import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import tosUploader from '../utils/tosUploader';
import './Workspace.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001/api';

function Workspace() {
  const navigate = useNavigate();
  const { userProfile, session, signOut, refreshProfile } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [selectedFile, setSelectedFile] = useState(null);
  const [targetLanguage, setTargetLanguage] = useState('zh');
  const [processing, setProcessing] = useState(false);
  const [processingStage, setProcessingStage] = useState('');
  const [processingProgress, setProcessingProgress] = useState(0);
  const [subtitles, setSubtitles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [replaceTerm, setReplaceTerm] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setErrorMessage('');
    }
  };

  const parseTimeToMs = (timeStr) => {
    const [time, ms] = timeStr.split(',');
    const [hours, minutes, seconds] = time.split(':').map(Number);
    return (hours * 3600 + minutes * 60 + seconds) * 1000 + parseInt(ms || 0);
  };

  const getAudioDuration = (audioFile) => {
    return new Promise((resolve, reject) => {
      const audio = new Audio();
      audio.preload = 'metadata';
      audio.onloadedmetadata = () => { URL.revokeObjectURL(audio.src); resolve(audio.duration); };
      audio.onerror = () => { URL.revokeObjectURL(audio.src); reject(new Error('无法获取音频时长')); };
      audio.src = URL.createObjectURL(audioFile);
    });
  };

  const handleStartConversion = async () => {
    if (!selectedFile) { setErrorMessage('请先选择音频或SRT文件'); return; }
    setProcessing(true); setErrorMessage(''); setProcessingProgress(0);
    let progressInterval = null;
    try {
      const isSrt = selectedFile.name.toLowerCase().endsWith('.srt');
      const isAudio = selectedFile.type.startsWith('audio/');

      if (isSrt) {
        setProcessingStage('正在读取SRT文件...'); setProcessingProgress(10);
        const srtContent = await selectedFile.text();
        setProcessingStage('正在翻译字幕...'); setProcessingProgress(30);
        const response = await axios.post(`${API_BASE_URL}/subtitle/translate-srt`,
          { srtContent, targetLanguage },
          { headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` }, timeout: 600000 });
        setProcessingProgress(100); setProcessingStage('翻译完成！');
        if (response.data.success) {
          const backendSubtitles = response.data.data.subtitles;
          const formattedSubtitles = backendSubtitles.map(sub => {
            const [start, end] = sub.time_range.split(' --> ');
            return { startTime: parseTimeToMs(start), endTime: parseTimeToMs(end), text: sub.text, translatedText: sub.trans_text };
          });
          setSubtitles(formattedSubtitles);
          if (refreshProfile) await refreshProfile();
        } else { throw new Error(response.data.error || 'SRT翻译失败'); }
        setProcessing(false);
        setTimeout(() => { setProcessingStage(''); setProcessingProgress(0); }, 2000);
      } else if (isAudio) {
        setProcessingStage('正在分析音频...');
        const audioDuration = await getAudioDuration(selectedFile);
        const audioSizeMB = (selectedFile.size / 1024 / 1024).toFixed(2);

        // 检查音频时长限制（60分钟 = 3600秒）
        if (audioDuration > 3600) {
          throw new Error(`音频时长超过限制。当前时长：${(audioDuration/60).toFixed(1)}分钟，最大支持：60分钟`);
        }

        setProcessingProgress(10);

        setProcessingStage('正在上传音频...'); setProcessingProgress(10);
        const audioFileName = `audio_${Date.now()}.wav`;
        const uploadResult = await tosUploader.uploadAudio(selectedFile, audioFileName, session?.access_token,
          (progress) => { setProcessingProgress(10 + Math.floor(progress * 40 / 100)); });
        const tosObjectKey = uploadResult.objectKey;

        setProcessingStage('正在生成字幕...'); setProcessingProgress(50);
        let currentProgress = 50;
        progressInterval = setInterval(() => {
          currentProgress += 1;
          if (currentProgress <= 95) {
            setProcessingProgress(currentProgress);
            if (currentProgress < 65) setProcessingStage('正在识别语音...');
            else if (currentProgress < 85) setProcessingStage('正在生成字幕...');
            else setProcessingStage('正在处理字幕时间轴...');
          } else { clearInterval(progressInterval); progressInterval = null; }
        }, 300);

        const response = await axios.post(`${API_BASE_URL}/subtitle/generate-from-tos`,
          { tosObjectKey, targetLanguage, durationSeconds: Math.ceil(audioDuration), fileSizeMB: audioSizeMB },
          { headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` }, timeout: 1200000 });

        if (progressInterval) { clearInterval(progressInterval); progressInterval = null; }
        if (response.data.success) {
          setSubtitles(response.data.data.translatedText || []);
          setProcessingProgress(100); setProcessingStage('完成！');
          setTimeout(() => { setProcessingStage(''); setProcessingProgress(0); }, 2000);
        } else { setErrorMessage(response.data.error || '字幕生成失败'); }
        setProcessing(false);
      }
    } catch (error) {
      if (progressInterval) { clearInterval(progressInterval); }
      console.error('转换失败:', error);
      let errorMsg = '转换失败，请重试';
      if (error.response) { errorMsg = error.response.data.error || error.response.data.message || `服务器错误 (${error.response.status})`; }
      else if (error.request) {
        if (error.message.includes('音频上传失败')) errorMsg = error.message;
        else if (error.message.includes('timeout')) errorMsg = '请求超时，请检查网络连接或稍后重试';
        else errorMsg = '网络错误，请检查连接后重试';
      } else { errorMsg = error.message || '转换失败，请重试'; }
      setErrorMessage(errorMsg); setProcessing(false); setProcessingStage(''); setProcessingProgress(0);
    }
  };
  const handleSubtitleEdit = (index, field, value) => {
    const newSubtitles = [...subtitles];
    newSubtitles[index][field] = value;
    setSubtitles(newSubtitles);
  };

  const handleFindReplace = () => {
    if (!searchTerm) return;
    setSubtitles(subtitles.map(subtitle => ({
      ...subtitle,
      translatedText: subtitle.translatedText.replaceAll(searchTerm, replaceTerm)
    })));
  };

  const handleExport = () => {
    let srtContent = '';
    subtitles.forEach((subtitle, index) => {
      srtContent += `${index + 1}\n${formatTime(subtitle.startTime)} --> ${formatTime(subtitle.endTime)}\n${subtitle.translatedText}\n\n`;
    });
    const blob = new Blob([srtContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'subtitle.srt'; a.click();
  };

  const formatTime = (ms) => {
    const hours = Math.floor(ms / 3600000);
    const minutes = Math.floor((ms % 3600000) / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    const milliseconds = ms % 1000;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')},${String(milliseconds).padStart(3, '0')}`;
  };

  const handleLogout = async () => {
    try { await signOut(); navigate('/'); } catch (error) { console.error('登出失败:', error); }
  };
  return (
    <div className="ws">
      <header className="ws-top">
        <div className="ws-top-left">
          <button onClick={() => navigate('/')} className="ws-top-btn">← 首页</button>
          <span className="ws-top-path"><span className="ws-top-sep">/</span> 工作台</span>
        </div>
        <div className="ws-top-right">
          {userProfile && <span className="ws-bal">¥{Number(userProfile.balance||0).toFixed(2)}</span>}
          <div className="theme-toggle" onClick={toggleTheme} title="切换主题">
            <span className={`theme-toggle-label theme-toggle-label--light ${theme === 'light' ? 'theme-toggle-label--active' : ''}`}>浅色</span>
            <span className={`theme-toggle-label theme-toggle-label--dark ${theme === 'dark' ? 'theme-toggle-label--active' : ''}`}>深色</span>
            <span className={`theme-toggle-indicator ${theme === 'dark' ? 'theme-toggle-indicator--dark' : ''}`} />
          </div>
          <button onClick={() => navigate('/profile')} className="ws-top-btn">个人中心</button>
          <button onClick={handleLogout} className="ws-top-btn ws-top-btn--red">退出</button>
        </div>
      </header>

      {errorMessage && (
        <div className="ws-err">
          <span>$ 错误：{errorMessage}</span>
          <button onClick={()=>setErrorMessage('')}>×</button>
        </div>
      )}

      <div className="ws-body">
        <aside className="ws-side">
          <div className="ws-side-inner">
            <div className="ws-term-head">
              <span className="ws-dot ws-dot--r"/><span className="ws-dot ws-dot--y"/><span className="ws-dot ws-dot--g"/>
              <span className="ws-term-title">控制台</span>
            </div>
            <div className="ws-side-content">
              <div className="ws-field">
                <div className="ws-prompt"><span className="ws-ps1">$</span> 上传文件</div>
                <label className="ws-upload">
                  <input type="file" accept="audio/*,.srt" onChange={handleFileSelect} hidden />
                  {selectedFile ? (
                    <div className="ws-file-ok">
                      <span className="ws-file-check">✓</span>
                      <div>
                        <div className="ws-file-name">{selectedFile.name}</div>
                        <div className="ws-file-size">{(selectedFile.size/1024/1024).toFixed(2)} MB</div>
                      </div>
                    </div>
                  ) : (
                    <div className="ws-file-ph">
                      <span className="ws-file-ph-icon">↑</span>
                      <span className="ws-file-ph-main">点击上传音频或 SRT 文件</span>
                      <span className="ws-file-ph-hint">支持 WAV / MP3 / M4A / AAC / SRT 格式</span>
                      <span className="ws-file-ph-hint">SRT 文件将直接进行翻译</span>
                    </div>
                  )}
                </label>
              </div>
              <div className="ws-field">
                <div className="ws-prompt"><span className="ws-ps1">$</span> 目标语言</div>
                <select value={targetLanguage} onChange={e => setTargetLanguage(e.target.value)} className="ws-sel">
                  <option value="zh">中文普通话</option>
                  <option value="en">英语</option>
                </select>
              </div>
              <button onClick={handleStartConversion} disabled={!selectedFile||processing||subtitles.length>0} className={`ws-go ${subtitles.length>0?'ws-go--done':''}`}>
                {processing ? (
                  <><span className="ws-go-spinner"/>{processingStage || '处理中...'}{processingProgress > 0 && ` ${processingProgress}%`}</>
                ) : (
                  <><span className="ws-ps1">▶</span> 生成字幕</>
                )}
              </button>
              {processing && (
                <div className="ws-prog">
                  <div className="ws-prog-track"><div className="ws-prog-fill" style={{width:`${processingProgress}%`}}/></div>
                </div>
              )}
              <button onClick={handleExport} disabled={subtitles.length===0} className={`ws-export ${subtitles.length>0?'ws-export--active':''}`}>导出 SRT 文件</button>

              {/* 搜索替换 */}
              <div className="ws-side-search">
                <div className="ws-prompt"><span className="ws-ps1">$</span> 查找替换</div>
                <input type="text" placeholder="查找文本" value={searchTerm} onChange={e=>setSearchTerm(e.target.value)} className="ws-sinput" disabled={subtitles.length===0}/>
                <input type="text" placeholder="替换为" value={replaceTerm} onChange={e=>setReplaceTerm(e.target.value)} className="ws-sinput" disabled={subtitles.length===0}/>
                <button onClick={handleFindReplace} className="ws-xbtn" disabled={subtitles.length===0||!searchTerm}>替换全部</button>
              </div>

              {/* 统计 */}
              <div className="ws-stats">
                <div className="ws-stat">
                  <span className="ws-stat-val">{selectedFile ? (selectedFile.size/1024/1024).toFixed(1) : '0'}</span>
                  <span className="ws-stat-unit">MB</span>
                  <span className="ws-stat-label">文件大小</span>
                </div>
                <div className="ws-stat">
                  <span className="ws-stat-val">{subtitles.length}</span>
                  <span className="ws-stat-unit">条</span>
                  <span className="ws-stat-label">字幕数</span>
                </div>
                <div className="ws-stat">
                  <span className="ws-stat-val">{subtitles.reduce((a,s) => a + (s.text?.length||0), 0)}</span>
                  <span className="ws-stat-unit">个</span>
                  <span className="ws-stat-label">字符</span>
                </div>
              </div>
            </div>
          </div>
        </aside>
        <main className="ws-main">
          <div className="ws-term-head">
            <span className="ws-term-title">字幕编辑器</span>
            {subtitles.length > 0 && <span className="ws-cnt">{subtitles.length} 条</span>}
          </div>
          {subtitles.length > 0 && (
            <div className="ws-sub-header">
              <span className="ws-sub-badge">翻译完成: {subtitles.length} / {subtitles.length}</span>
            </div>
          )}
          <div className="ws-subs">
            {subtitles.length > 0 ? (
              <div className="ws-sub-list">
                {subtitles.map((subtitle, index) => (
                  <div key={index} className="ws-sub-row">
                    <div className="ws-sub-time">
                      <div>#{index+1} {formatTime(subtitle.startTime)}</div>
                      <div className="ws-sub-time-end">→ {formatTime(subtitle.endTime)}</div>
                    </div>
                    <div className="ws-sub-texts">
                      <textarea value={subtitle.text} onChange={e => handleSubtitleEdit(index,'text',e.target.value)} onClick={e=>e.stopPropagation()} rows="2" className="ws-sub-ta"/>
                      <textarea value={subtitle.translatedText} onChange={e => handleSubtitleEdit(index,'translatedText',e.target.value)} onClick={e=>e.stopPropagation()} rows="2" className="ws-sub-ta ws-sub-ta--t" placeholder="译文"/>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="ws-sub-empty">
                <pre className="ws-sub-empty-art">{`  _____\n |  _  |\n | |_| |\n |_____|`}</pre>
                <p className="ws-sub-empty-t">$ 等待输入...</p>
                <p className="ws-sub-empty-h">上传文件后点击「生成字幕」</p>
              </div>
            )}
          </div>
        </main>
      </div>

      <footer className="ws-foot">
        <span>就绪</span>
        <span>{subtitles.length} 条字幕</span>
      </footer>
    </div>
  );
}

export default Workspace;
